import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import * as Sentry from '@sentry/react';
import axios from 'axios';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import { Api } from 'src/models/Api.model';
import { I18NextNS } from 'src/models/Language.model';

import { captureError } from 'src/utils/errorReporting';

// 1m
const timeout = 1000 * 60;

export default function useCheckDeployment() {
  const { logOut } = useRemoteBoundStore();
  const history = useHistory();

  const {
    t,
    i18n: { language },
  } = useTranslation(I18NextNS.COMMON);

  const previousFileContentRef = useRef<string | null>(null);
  const intervalRef = useRef<number | null>(null);

  const startDeploymentWatcher = useCallback(() => {
    // already started?
    if (intervalRef.current) {
      return;
    }

    const checkIndexFileChange = async () => {
      try {
        const response = await axios.get(`/?ts=${Date.now().toString()}`, {
          headers: {
            'Cache-Control': 'no-cache', // Bypass cache
          },
        });

        const currentFileContent = response.data;

        if (
          previousFileContentRef.current &&
          previousFileContentRef.current !== currentFileContent
        ) {
          Sentry.logger.info('New deployment detected');
          const isOnLoginPage = history.location.pathname === '/login';
          Api.alertBox({
            title: t('bootbox.title.Warning'),
            message: isOnLoginPage
              ? t('message.error.newDeploymentDetectedRefresh')
              : t('message.error.newDeploymentDetected'),
            locale: language,
            callback: () => {
              logOut(true);
            },
            type: 'warning',
          });
        }

        previousFileContentRef.current = currentFileContent;
      } catch (err) {
        console.error(err);
        captureError(err, 'Check Deployment: Failed to check index file');
      }
    };

    // initial check
    checkIndexFileChange();

    // start a new interval
    intervalRef.current = window.setInterval(() => {
      checkIndexFileChange();
    }, timeout);

    console.log('Deployment watcher started');
  }, [language, logOut, t, history]);

  const stopDeploymentWatcher = useCallback(() => {
    // not started?
    if (!intervalRef.current) {
      return;
    }

    // stop the interval
    window.clearInterval(intervalRef.current);
    intervalRef.current = null;

    console.log('Deployment watcher stopped');
  }, []);

  return { startDeploymentWatcher, stopDeploymentWatcher };
}
